import { type FC, type SyntheticEvent, useCallback, useRef, useState } from "react";
import Modal from "~/common/components/Modal";

import useMediaQuery from "~/common/hooks/useMediaQuery";
import { AlertTitle, Button, Stack, TextField } from "~/common/mui";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { trackLoginAttempt, trackLoginSuccess } from "~/app/analytics/ga/ga";
import { sendLoginRequest } from "~/features/auth/login/api/DoLogin";
import { doOnLoginAction } from "~/features/auth/login/loginUtils";
import { Link } from "~/common/components/Link";
import { redirectToSrp } from "~/utils/redirectionUtil";
import { Alert } from "~/common/mui/Alert/Alert";

interface Props {
  initialEmail: string;
  onResetClick: (email: string) => void;
  // use to configure an alternative eventAction in GA
  alternativeGAAction?: string;
}

const MediasBusinessLoginPopup: FC<Props> = ({ initialEmail, onResetClick, alternativeGAAction }) => {
  const { t } = useTranslation();

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwdRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState("");
  const [inFlight, setInFlight] = useState(false);
  const [popupVisible, setPopupVisible] = useState(true);

  const invalidInputs = error === "login.error.credentials";

  const resetCb = () => {
    let email = "";
    if (emailRef.current) {
      email = emailRef.current.value;
    }
    onResetClick(email);
  };

  const getTimezoneString = () => {
    const offsetInMinutes = new Date().getTimezoneOffset() * -1;
    return offsetInMinutes.toString();
  };

  const doLogin = useCallback(async () => {
    if (!emailRef.current || !passwdRef.current) {
      return;
    }

    try {
      trackLoginAttempt(alternativeGAAction);
      setInFlight(true);
      const result = await sendLoginRequest({
        login: emailRef.current.value,
        password: passwdRef.current.value,
        timezone: getTimezoneString(),
      });
      if (!result.error) {
        trackLoginSuccess(alternativeGAAction);
        doOnLoginAction(result.redirectUrl);
      } else {
        setInFlight(false);
        setError(result.error);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
      setInFlight(false);
    }
  }, [alternativeGAAction]);

  const resetPassword = (
    <div className={"d-flex flex-justify-between flex-align-center mt-2 mb-3"}>
      <Link onClick={resetCb} data-test-id={"forgot-pw-link"}>
        {t("page.login.forgottenPwdLink")}
      </Link>
    </div>
  );

  const onSubmit = useCallback(
    (e: SyntheticEvent<unknown>) => {
      e.preventDefault();
      // noinspection JSIgnoredPromiseFromCall
      doLogin();
    },
    [doLogin],
  );

  const onClose = () => {
    setPopupVisible(false);
    redirectToSrp();
  };

  const footer = (
    <Stack direction={"row"} gap={1}>
      <Button color={"primary"} variant={"text"} data-test-id={"login-cancel-button"} onClick={onClose}>
        {t("cancel")}
      </Button>
      <Button
        color={"primary"}
        loading={inFlight}
        data-test-id={"login-signin-button"}
        onClick={onSubmit}
        fullWidth={!isDesktop}
      >
        {t("header.link.signIn")}
      </Button>
    </Stack>
  );

  return (
    popupVisible && (
      <Modal onClose={onClose} footerChildren={footer} title={t("header.link.signIn")}>
        <div style={{ paddingBottom: "16px" }}>
          <Alert severity={"info"}>
            <AlertTitle>{t("popup.signout.info.header")}</AlertTitle>
            {t("popup.signin.info.message")}
          </Alert>
        </div>
        <form onSubmit={onSubmit}>
          <button hidden type={"submit"} />
          <div className={"mt-3 mb-2 email-field"}>
            <TextField
              fullWidth
              inputRef={emailRef}
              defaultValue={initialEmail}
              name={"j_username"}
              inputProps={{ "data-test-id": "login-input" }}
              label={t("page.login.email")}
              error={invalidInputs}
            />
          </div>
          <div className={"password-field"}>
            <TextField
              fullWidth
              inputRef={passwdRef}
              inputProps={{ "data-test-id": "password-input" }}
              name={"j_password"}
              label={t("page.login.password")}
              type={"password"}
              error={invalidInputs}
            />
          </div>
          {error && (
            <Alert className={"my-2"} severity={"error"}>
              {t(error)}
            </Alert>
          )}
          {isDesktop && resetPassword}
        </form>
      </Modal>
    )
  );
};
export default MediasBusinessLoginPopup;
